/**
 * Moteur du contrôle de vidéo LinkedIn : pur, sans React.
 *
 * Deux sources officielles, lues le jour de la construction, et l'outil les distingue :
 * - Membres (profil, groupe, Page via la zone de partage) : aide « Partager des vidéos sur LinkedIn »
 *   (answer/a7486279) : 3 s (2 s depuis l'appli mobile) à 15 min, 75 Ko à 5 Go, 256 × 144 à 4 096 × 2 304,
 *   ratio 1:2,4 à 2,4:1, 10 à 60 images/s, 192 kbit/s à 30 Mbit/s. Aucun format de fichier cité.
 * - Pages et Pages Carrières : aide « Spécifications vidéo » (answer/a1311816) : mêmes bornes, mais 10 min
 *   maximum, une liste de formats, et « AVI, QuickTime et MOV ne sont plus pris en charge ».
 * Les règles `editorial` sont des repères NeverZero, étiquetés comme tels.
 */
import type { Severity } from '@/lib/postSimulator';
import type { CheckSource } from '@/lib/carouselCheck';

export type Audience = 'member' | 'page';
export type Upload = 'desktop' | 'mobile';
export type Container = 'mp4' | 'mov' | 'avi' | 'mkv-webm' | 'other';
export type Tri = 'yes' | 'no' | 'unknown';

export const AUDIENCES: Record<Audience, string> = {
  member: 'Mon profil (ou la zone de partage)',
  page: 'Page entreprise ou Page Carrières',
};

export const UPLOADS: Record<Upload, string> = {
  desktop: 'Ordinateur',
  mobile: 'Appli mobile',
};

export const CONTAINERS: Record<Container, string> = {
  mp4: 'MP4',
  mov: 'MOV (iPhone, QuickTime)',
  avi: 'AVI',
  'mkv-webm': 'MKV ou WebM',
  other: 'Autre / je ne sais pas',
};

export const SIZE_PRESETS: { label: string; width: string; height: string }[] = [
  { label: '1920 × 1080 (16:9)', width: '1920', height: '1080' },
  { label: '1080 × 1080 (carré)', width: '1080', height: '1080' },
  { label: '1080 × 1350 (4:5)', width: '1080', height: '1350' },
  { label: '1080 × 1920 (9:16)', width: '1080', height: '1920' },
  { label: '2304 × 4096 (9:16 en pleine définition)', width: '2304', height: '4096' },
];

export interface VideoInputs {
  audience: Audience;
  upload: Upload;
  container: Container | null;
  duration: string;
  width: string;
  height: string;
  size: string;
  fps: string;
  bitrate: string;
  captions: Tri;
  edgesClear: boolean;
  hook: Tri;
}

export const EMPTY_INPUTS: VideoInputs = {
  audience: 'member',
  upload: 'desktop',
  container: null,
  duration: '',
  width: '',
  height: '',
  size: '',
  fps: '',
  bitrate: '',
  captions: 'unknown',
  edgesClear: true,
  hook: 'unknown',
};

export interface VideoCheck {
  id: string;
  source: CheckSource;
  severity: Severity;
  title: string;
  detail: string;
  fix?: string;
}

export type Verdict = 'incomplete' | 'blocked' | 'fix' | 'ready';

export interface VideoResult {
  verdict: Verdict;
  headline: string;
  summary: string;
  checks: VideoCheck[];
  problems: number;
  warnings: number;
  passed: number;
  /** Poids estimé à partir du débit et de la durée, en Mo, quand les deux sont saisis. */
  estimatedMb: number | null;
  /** Débit moyen déduit du poids et de la durée, en Mbit/s, quand le débit n'est pas saisi. */
  impliedMbps: number | null;
}

export const LIMITS = {
  minSeconds: { desktop: 3, mobile: 2 },
  maxSeconds: { member: 15 * 60, page: 10 * 60 },
  minMb: 75 / 1024,
  maxMb: 5 * 1024,
  minW: 256,
  minH: 144,
  maxW: 4096,
  maxH: 2304,
  ratioMin: 1 / 2.4,
  ratioMax: 2.4,
  fpsMin: 10,
  fpsMax: 60,
  mbpsMin: 0.192,
  mbpsMax: 30,
} as const;

function frNumber(n: number, digits = 0): string {
  return n.toLocaleString('fr-FR', { maximumFractionDigits: digits });
}

function toNumber(raw: string): number | null {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** « 90 », « 1:30 », « 01:30:00 » → secondes. Autre chose → null. */
export function parseDuration(raw: string): number | null {
  const text = raw.trim();
  if (text === '') return null;
  if (text.includes(':')) {
    const parts = text.split(':').map((p) => p.trim());
    if (parts.length > 3 || parts.some((p) => !/^\d+(?:[.,]\d+)?$/.test(p))) return null;
    return parts.reduce((total, p) => total * 60 + Number(p.replace(',', '.')), 0);
  }
  return toNumber(text);
}

/** « 850 », « 850 Mo », « 1,2 Go », « 600 Ko » → Mo (binaire, comme l'affiche l'explorateur de fichiers). */
export function parseSizeMb(raw: string): number | null {
  const match = raw.trim().toLowerCase().match(/^([\d\s.,]+)\s*(ko|kb|mo|mb|go|gb)?$/);
  if (!match) return null;
  const value = toNumber(match[1]);
  if (value === null) return null;
  const unit = match[2] ?? 'mo';
  if (unit === 'ko' || unit === 'kb') return value / 1024;
  if (unit === 'go' || unit === 'gb') return value * 1024;
  return value;
}

export function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return m > 0 ? `${m} min ${String(rest).padStart(2, '0')} s` : `${s} s`;
}

function formatSize(mb: number): string {
  return mb >= 1024 ? `${frNumber(mb / 1024, 2)} Go` : mb >= 1 ? `${frNumber(mb, 1)} Mo` : `${frNumber(mb * 1024, 0)} Ko`;
}

export function ratioLabel(width: number, height: number): string {
  const r = width / height;
  if (Math.abs(r - 1) < 0.02) return '1:1 (carré)';
  if (Math.abs(r - 0.8) < 0.02) return '4:5 (portrait)';
  if (Math.abs(r - 9 / 16) < 0.02) return '9:16 (vertical)';
  if (Math.abs(r - 16 / 9) < 0.02) return '16:9 (paysage)';
  if (Math.abs(r - 4 / 3) < 0.02) return '4:3 (paysage)';
  return r >= 1 ? `${frNumber(r, 2)}:1 (paysage)` : `1:${frNumber(1 / r, 2)} (portrait)`;
}

export function evaluate(input: VideoInputs): VideoResult {
  const seconds = parseDuration(input.duration);
  const width = toNumber(input.width);
  const height = toNumber(input.height);
  const sizeMb = parseSizeMb(input.size);
  const fps = toNumber(input.fps);
  const mbps = toNumber(input.bitrate);
  const page = input.audience === 'page';
  const checks: VideoCheck[] = [];

  const empty: VideoResult = {
    verdict: 'incomplete',
    headline: '',
    summary: '',
    checks,
    problems: 0,
    warnings: 0,
    passed: 0,
    estimatedMb: null,
    impliedMbps: null,
  };
  const hasAny =
    input.container !== null || seconds !== null || width !== null || height !== null || sizeMb !== null || fps !== null || mbps !== null;
  if (!hasAny) return empty;

  const maxSeconds = LIMITS.maxSeconds[input.audience];
  const minSeconds = LIMITS.minSeconds[input.upload];
  const maxLabel = page ? '10 minutes' : '15 minutes';

  // 1. Format de fichier : la page « Pages » en liste, l'aide « membres » n'en cite aucun.
  if (input.container) {
    if (input.container === 'mp4') {
      checks.push({
        id: 'format',
        source: 'official',
        severity: 'ok',
        title: 'MP4',
        detail: page
          ? 'MP4 figure dans la liste des formats acceptés pour les Pages.'
          : 'L’aide LinkedIn destinée aux membres ne cite aucun format de fichier. MP4 (H.264) est le choix le plus sûr, mais ce n’est pas écrit.',
      });
    } else if (input.container === 'mov' || input.container === 'avi') {
      const name = input.container === 'mov' ? 'MOV' : 'AVI';
      checks.push(
        page
          ? {
              id: 'format',
              source: 'official',
              severity: 'problem',
              title: `${name} : plus pris en charge sur les Pages`,
              detail: 'L’aide LinkedIn pour les Pages écrit : « Nous ne prenons plus en charge les fichiers AVI, QuickTime ou MOV. »',
              fix: 'Exportez en MP4 (H.264) depuis votre logiciel de montage, ou convertissez le fichier avec HandBrake.',
            }
          : {
              id: 'format',
              source: 'official',
              severity: 'warning',
              title: `${name} : non garanti pour un profil`,
              detail: `L’aide destinée aux membres ne cite aucun format, mais celle des Pages écrit que ${name} n’est plus pris en charge. Le risque d’un échec d’envoi est réel.`,
              fix: 'Exportez en MP4 (H.264) avant l’envoi. Sur iPhone, un export depuis Photos en « Compatibilité maximale » donne un MP4/H.264.',
            },
      );
    } else if (input.container === 'mkv-webm') {
      checks.push({
        id: 'format',
        source: 'official',
        severity: page ? 'ok' : 'warning',
        title: page ? 'MKV ou WebM : accepté sur les Pages' : 'MKV ou WebM : non garanti pour un profil',
        detail: page
          ? 'MKV et WebM figurent dans la liste des formats de l’aide LinkedIn pour les Pages.'
          : 'MKV et WebM sont listés pour les Pages, mais l’aide destinée aux membres ne cite aucun format.',
        fix: page ? undefined : 'Exportez en MP4 (H.264) pour ne rien laisser au hasard.',
      });
    } else {
      checks.push({
        id: 'format',
        source: 'official',
        severity: 'warning',
        title: 'Format à vérifier',
        detail: page
          ? 'Formats listés pour les Pages : MP4, MPEG-1, MPEG-4, MKV, WebM, FLV, ASF, WMV (codecs H.264, VP8, VP9, entre autres). Un autre conteneur risque d’être refusé.'
          : 'L’aide destinée aux membres ne cite aucun format. Un autre conteneur que MP4 risque d’être refusé.',
        fix: 'Exportez en MP4 (H.264, son AAC) : c’est ce que les outils de montage proposent par défaut.',
      });
    }
  }

  // 2. Durée.
  if (seconds !== null) {
    if (seconds <= 0) {
      checks.push({
        id: 'duration',
        source: 'official',
        severity: 'problem',
        title: 'Durée invalide',
        detail: 'Saisissez la durée en secondes (90) ou en minutes:secondes (1:30).',
      });
    } else if (seconds < minSeconds) {
      checks.push({
        id: 'duration',
        source: 'official',
        severity: 'problem',
        title: `${formatDuration(seconds)} : trop court`,
        detail:
          input.upload === 'mobile' && !page
            ? 'Depuis l’appli mobile, LinkedIn exige 2 secondes au moins.'
            : `LinkedIn exige ${minSeconds} secondes au moins${page ? '' : ' depuis un ordinateur (2 secondes depuis l’appli mobile)'}.`,
        fix: 'Allongez la vidéo, ou publiez une image : une vidéo de cette durée n’a pas le temps de dire quelque chose.',
      });
    } else if (seconds > maxSeconds) {
      checks.push({
        id: 'duration',
        source: 'official',
        severity: 'problem',
        title: `${formatDuration(seconds)} : au-dessus du plafond de ${maxLabel}`,
        detail: `LinkedIn refuse une vidéo de plus de ${maxLabel}${page ? ' sur une Page' : ''}. Vous dépassez de ${formatDuration(seconds - maxSeconds)}.`,
        fix: page
          ? `Coupez à 10 minutes, ou publiez depuis un profil, où le plafond est de 15 minutes.`
          : 'Découpez en deux vidéos, ou coupez ce qui n’apporte pas à l’idée.',
      });
    } else {
      checks.push({
        id: 'duration',
        source: 'official',
        severity: 'ok',
        title: `${formatDuration(seconds)} : dans les limites`,
        detail: `Entre ${minSeconds} secondes et ${maxLabel}${page ? ' (10 minutes sur une Page, 15 sur un profil)' : ''}.`,
      });
    }
  }

  // 3. Dimensions : résolution et ratio.
  let ratio: number | null = null;
  if (width !== null || height !== null) {
    if (width === null || height === null || width <= 0 || height <= 0) {
      checks.push({
        id: 'dimensions',
        source: 'official',
        severity: 'warning',
        title: 'Dimensions incomplètes',
        detail: 'Renseignez la largeur et la hauteur de la vidéo en pixels (clic droit sur le fichier, Propriétés, Détails).',
      });
    } else {
      ratio = width / height;
      const long = Math.max(width, height);
      const short = Math.min(width, height);
      const portrait = height > width;

      if (ratio > LIMITS.ratioMax + 0.005 || ratio < LIMITS.ratioMin - 0.005) {
        checks.push({
          id: 'ratio',
          source: 'official',
          severity: 'problem',
          title: `Ratio ${ratioLabel(width, height)} : hors de la plage 1:2,4 à 2,4:1`,
          detail: `LinkedIn n’accepte que les ratios compris entre 1:2,4 (très vertical) et 2,4:1 (très large). ${frNumber(width)} × ${frNumber(height)} est ${ratio > 1 ? 'trop large' : 'trop haut'}.`,
          fix: ratio > 1
            ? `Recadrez ou ajoutez des bandes : pour ${frNumber(width)} px de large, la hauteur doit atteindre ${frNumber(Math.ceil(width / LIMITS.ratioMax))} px.`
            : `Recadrez ou ajoutez des bandes : pour ${frNumber(height)} px de haut, la largeur doit atteindre ${frNumber(Math.ceil(height / LIMITS.ratioMax))} px.`,
        });
      } else {
        checks.push({
          id: 'ratio',
          source: 'official',
          severity: 'ok',
          title: `Ratio ${ratioLabel(width, height)}`,
          detail: 'Dans la plage acceptée, de 1:2,4 à 2,4:1. LinkedIn ne recommande aucun ratio précis dans son aide.',
        });
      }

      if (short < (portrait ? LIMITS.minW : LIMITS.minH) || long < (portrait ? LIMITS.minH : LIMITS.minW)) {
        checks.push({
          id: 'resolution',
          source: 'official',
          severity: 'problem',
          title: `${frNumber(width)} × ${frNumber(height)} : sous la définition minimale`,
          detail: 'LinkedIn exige au moins 256 × 144 pixels (144 × 256 en vertical).',
          fix: 'Réexportez à la définition d’origine, ou à 1280 × 720 au moins.',
        });
      } else if (portrait ? height > LIMITS.maxW || width > LIMITS.maxW : width > LIMITS.maxW || height > LIMITS.maxH) {
        checks.push({
          id: 'resolution',
          source: 'official',
          severity: 'problem',
          title: `${frNumber(width)} × ${frNumber(height)} : au-dessus de la définition maximale`,
          detail: 'LinkedIn accepte jusqu’à 4 096 × 2 304 pixels.',
          fix: portrait ? 'Exportez en 1080 × 1920, qui reste très en dessous des limites.' : 'Exportez en 1920 × 1080, ou en 3840 × 2160 si vous tenez à la 4K (hauteur maximale : 2 304).',
        });
      } else if (portrait && height > LIMITS.maxH) {
        checks.push({
          id: 'resolution',
          source: 'official',
          severity: 'warning',
          title: `${frNumber(width)} × ${frNumber(height)} : la limite ne dit pas si elle se lit en vertical`,
          detail: 'L’aide donne « 4 096 × 2 304 » sans préciser l’orientation. Lue à la lettre, une vidéo verticale de plus de 2 304 px de haut dépasse la limite (c’est le cas d’un 9:16 en 2 304 × 4 096), et l’aide ne tranche pas.',
          fix: 'Exportez en 1080 × 1920 : le rendu est net sur un téléphone et reste largement dans les deux lectures.',
        });
      } else {
        checks.push({
          id: 'resolution',
          source: 'official',
          severity: 'ok',
          title: `${frNumber(width)} × ${frNumber(height)} : définition acceptée`,
          detail: 'Entre 256 × 144 et 4 096 × 2 304 pixels.',
        });
        if (short < 720) {
          checks.push({
            id: 'sharp',
            source: 'editorial',
            severity: 'warning',
            title: `Petit côté de ${frNumber(short)} px : risque de flou`,
            detail: 'Accepté par LinkedIn, mais une vidéo sous 720 px sur son petit côté est agrandie à l’affichage et perd en netteté, surtout sur un grand écran. C’est un repère NeverZero.',
            fix: 'Réexportez à 1280 × 720 ou 1080 × 1920 si le fichier source le permet.',
          });
        }
      }
    }
  }

  // 4. Cadence.
  if (fps !== null) {
    if (fps < LIMITS.fpsMin || fps > LIMITS.fpsMax) {
      checks.push({
        id: 'fps',
        source: 'official',
        severity: 'problem',
        title: `${frNumber(fps, 2)} images/s : hors de la plage 10 à 60`,
        detail: 'LinkedIn accepte de 10 à 60 images par seconde.',
        fix: fps > LIMITS.fpsMax ? 'Exportez à 60 images/s au maximum (30 suffit pour une vidéo de parole).' : 'Réexportez à 24 ou 30 images/s.',
      });
    } else {
      checks.push({
        id: 'fps',
        source: 'official',
        severity: 'ok',
        title: `${frNumber(fps, 2)} images/s`,
        detail: 'Dans la plage acceptée, de 10 à 60 images par seconde.',
      });
    }
  }

  // 5. Débit, poids, et leur recoupement.
  let estimatedMb: number | null = null;
  let impliedMbps: number | null = null;
  if (mbps !== null) {
    if (mbps <= 0) {
      checks.push({
        id: 'bitrate',
        source: 'official',
        severity: 'problem',
        title: 'Débit invalide',
        detail: 'Saisissez le débit en Mbit/s, par exemple 8 ou 0,5.',
      });
    } else if (mbps > LIMITS.mbpsMax) {
      checks.push({
        id: 'bitrate',
        source: 'official',
        severity: 'problem',
        title: `${frNumber(mbps, 1)} Mbit/s : au-dessus du plafond de 30`,
        detail: 'LinkedIn accepte de 192 kbit/s à 30 Mbit/s.',
        fix: 'Réexportez avec un débit cible de 8 à 12 Mbit/s pour du 1080p : la différence visuelle avec 40 Mbit/s est faible sur un fil d’actualité.',
      });
    } else if (mbps < LIMITS.mbpsMin) {
      checks.push({
        id: 'bitrate',
        source: 'official',
        severity: 'problem',
        title: `${frNumber(mbps * 1000)} kbit/s : sous le minimum de 192 kbit/s`,
        detail: 'LinkedIn exige au moins 192 kbit/s.',
        fix: 'Relevez le débit à l’export : en dessous, l’image se dégrade avant même l’envoi.',
      });
    } else {
      checks.push({
        id: 'bitrate',
        source: 'official',
        severity: 'ok',
        title: `${frNumber(mbps, 2)} Mbit/s`,
        detail: 'Dans la plage acceptée, de 192 kbit/s à 30 Mbit/s.',
      });
    }
  }

  if (mbps !== null && mbps > 0 && seconds !== null && seconds > 0) {
    estimatedMb = (mbps * seconds) / 8;
  }
  if (sizeMb !== null && sizeMb > 0 && seconds !== null && seconds > 0 && mbps === null) {
    impliedMbps = (sizeMb * 1.048576 * 8) / seconds;
    if (impliedMbps > LIMITS.mbpsMax) {
      checks.push({
        id: 'implied',
        source: 'official',
        severity: 'problem',
        title: `Débit moyen déduit : ${frNumber(impliedMbps, 1)} Mbit/s, au-dessus du plafond de 30`,
        detail: `${formatSize(sizeMb)} sur ${formatDuration(seconds)} donnent ${frNumber(impliedMbps, 1)} Mbit/s en moyenne, alors que LinkedIn plafonne à 30 Mbit/s. Le fichier est trop lourd pour sa durée, pas trop long.`,
        fix: `Réexportez à 8 à 12 Mbit/s : cette vidéo pèserait alors environ ${formatSize((10 * seconds) / 8)}.`,
      });
    } else if (impliedMbps < LIMITS.mbpsMin) {
      checks.push({
        id: 'implied',
        source: 'official',
        severity: 'problem',
        title: `Débit moyen déduit : ${frNumber(impliedMbps * 1000)} kbit/s, sous le minimum de 192`,
        detail: `${formatSize(sizeMb)} sur ${formatDuration(seconds)} donnent un débit moyen sous le minimum de LinkedIn.`,
        fix: 'Vérifiez la durée saisie, ou réexportez avec un débit plus élevé.',
      });
    } else {
      checks.push({
        id: 'implied',
        source: 'official',
        severity: 'ok',
        title: `Débit moyen déduit : ${frNumber(impliedMbps, 1)} Mbit/s`,
        detail: `${formatSize(sizeMb)} sur ${formatDuration(seconds)}, ce qui reste dans la plage de 192 kbit/s à 30 Mbit/s.`,
      });
    }
  }

  if (sizeMb !== null) {
    if (sizeMb <= 0) {
      checks.push({
        id: 'weight',
        source: 'official',
        severity: 'problem',
        title: 'Poids invalide',
        detail: 'Saisissez le poids, par exemple 850 Mo, 1,2 Go ou 600 Ko.',
      });
    } else if (sizeMb > LIMITS.maxMb) {
      checks.push({
        id: 'weight',
        source: 'official',
        severity: 'problem',
        title: `${formatSize(sizeMb)} : au-dessus du plafond de 5 Go`,
        detail: `LinkedIn refuse un fichier de plus de 5 Go. Vous dépassez de ${formatSize(sizeMb - LIMITS.maxMb)}.`,
        fix: 'Réexportez avec un débit plus bas. À 30 Mbit/s, le maximum autorisé, 15 minutes pèsent environ 3,3 Go : un fichier plus lourd a un débit hors des clous.',
      });
    } else if (sizeMb < LIMITS.minMb) {
      checks.push({
        id: 'weight',
        source: 'official',
        severity: 'problem',
        title: `${formatSize(sizeMb)} : sous le minimum de 75 Ko`,
        detail: 'LinkedIn exige au moins 75 Ko.',
        fix: 'Vérifiez que c’est bien le bon fichier, et pas une vignette ou un export interrompu.',
      });
    } else {
      checks.push({
        id: 'weight',
        source: 'official',
        severity: 'ok',
        title: `${formatSize(sizeMb)}`,
        detail: 'Entre 75 Ko et 5 Go.',
      });
    }

    // Recoupement poids / (débit × durée) : un écart important signale une saisie fausse ou un débit variable.
    if (estimatedMb !== null && sizeMb > 0 && (sizeMb > estimatedMb * 1.5 || sizeMb < estimatedMb * 0.5)) {
      checks.push({
        id: 'crosscheck',
        source: 'editorial',
        severity: 'warning',
        title: 'Le poids ne colle pas avec le débit et la durée',
        detail: `${frNumber(mbps ?? 0, 1)} Mbit/s pendant ${formatDuration(seconds ?? 0)} donnent environ ${formatSize(estimatedMb)} (la piste son ajoute un peu), et vous annoncez ${formatSize(sizeMb)}. Soit une valeur est mal saisie, soit le débit est variable : le débit saisi est une moyenne, pas un plafond.`,
        fix: 'Relisez les trois valeurs dans les propriétés du fichier. Si le débit varie, c’est son pic qui compte pour la limite de 30 Mbit/s.',
      });
    }
  } else if (estimatedMb !== null) {
    checks.push({
      id: 'estimate',
      source: 'editorial',
      severity: estimatedMb > LIMITS.maxMb ? 'problem' : 'ok',
      title: `Poids estimé : ${formatSize(estimatedMb)}`,
      detail:
        estimatedMb > LIMITS.maxMb
          ? 'Estimation à partir du débit et de la durée : le fichier dépasserait 5 Go.'
          : 'Estimation à partir du débit et de la durée (débit × durée ÷ 8, hors piste son) : sous le plafond de 5 Go.',
    });
  }

  // 6. Bords de l'image (officiel).
  if (!input.edgesClear) {
    checks.push({
      id: 'edges',
      source: 'official',
      severity: 'warning',
      title: 'Texte ou logo près des bords',
      detail: 'LinkedIn demande de ne placer ni éléments clés, ni texte, ni logos sur les bords (haut, bas, côtés), car l’interface peut les recouvrir.',
      fix: 'Rentrez les sous-titres et le logo d’environ 5 % de la largeur et de la hauteur. Relisez l’aperçu avant de publier.',
    });
  } else if (checks.length > 0) {
    checks.push({
      id: 'edges',
      source: 'official',
      severity: 'ok',
      title: 'Bords libres',
      detail: 'Rien d’essentiel près des bords : l’interface de LinkedIn ne masquera rien d’important.',
    });
  }

  // 7. Sous-titres et accroche (repères éditoriaux).
  if (input.captions === 'no') {
    checks.push({
      id: 'captions',
      source: 'editorial',
      severity: 'warning',
      title: 'Pas de sous-titres',
      detail: 'Beaucoup regardent le fil sans le son. LinkedIn ne donne aucun chiffre sur cette part, mais une vidéo de parole sans texte est inutile pour qui lit en silence.',
      fix: 'À l’envoi depuis un ordinateur, générez les sous-titres automatiques (10 langues prises en charge), relisez-les, ou chargez un fichier de sous-titres.',
    });
  } else if (input.captions === 'yes') {
    checks.push({
      id: 'captions',
      source: 'editorial',
      severity: 'ok',
      title: 'Sous-titres prévus',
      detail: 'Relisez-les avant de publier : les noms propres et le vocabulaire de métier sont les premiers à être mal transcrits.',
    });
  }
  if (input.hook === 'no') {
    checks.push({
      id: 'hook',
      source: 'editorial',
      severity: 'problem',
      title: 'Les 3 premières secondes ne disent pas l’idée',
      detail: 'Le format ne fait que gagner le droit d’être regardé. Une vidéo parfaitement exportée dont l’ouverture n’accroche personne reçoit aussi peu de réactions qu’un texte vide, et ce sont les réactions de la première heure qui décident de la diffusion.',
      fix: 'Commencez par la conclusion ou par le résultat, écrit à l’écran et dit à voix haute, avant tout « bonjour ». Coupez l’intro de logo.',
    });
  } else if (input.hook === 'yes') {
    checks.push({
      id: 'hook',
      source: 'editorial',
      severity: 'ok',
      title: 'L’idée est donnée d’entrée',
      detail: 'Le lecteur sait en trois secondes pourquoi rester. Reste à lui donner une raison de réagir dans la première heure.',
    });
  }

  const problems = checks.filter((c) => c.severity === 'problem').length;
  const warnings = checks.filter((c) => c.severity === 'warning').length;
  const passed = checks.filter((c) => c.severity === 'ok').length;
  if (checks.length === 0) return empty;

  const officialBlock = checks.some((c) => c.source === 'official' && c.severity === 'problem');
  let verdict: Verdict;
  let headline: string;
  let summary: string;
  if (officialBlock) {
    verdict = 'blocked';
    headline = 'LinkedIn risque de refuser cette vidéo';
    summary = `${problems} point${problems > 1 ? 's' : ''} à corriger, dont au moins une limite écrite dans l’aide LinkedIn.`;
  } else if (problems > 0) {
    verdict = 'fix';
    headline = 'Elle passera, mais ne retiendra personne';
    summary = `La vidéo respecte les limites de LinkedIn. ${problems} point${problems > 1 ? 's' : ''} à corriger avant de publier.`;
  } else if (warnings > 0) {
    verdict = 'fix';
    headline = 'Prête, avec quelques réglages';
    summary = `Aucun blocage. ${warnings} point${warnings > 1 ? 's' : ''} à surveiller, ${passed} validé${passed > 1 ? 's' : ''}.`;
  } else {
    verdict = 'ready';
    headline = 'Prête à publier';
    summary = `${passed} contrôle${passed > 1 ? 's' : ''} validé${passed > 1 ? 's' : ''}, aucun point à corriger. Le format gagne le droit d’être regardé : la diffusion se joue ensuite dans la première heure.`;
  }

  const order: Record<Severity, number> = { problem: 0, warning: 1, ok: 2 };
  const sorted = [...checks].sort((a, b) => order[a.severity] - order[b.severity]);

  return { verdict, headline, summary, checks: sorted, problems, warnings, passed, estimatedMb, impliedMbps };
}

export function reportText(result: VideoResult, input: VideoInputs): string {
  const label: Record<Severity, string> = { problem: 'À corriger', warning: 'À surveiller', ok: 'OK' };
  const src: Record<CheckSource, string> = { official: 'règle LinkedIn', editorial: 'repère NeverZero' };
  const head = `${result.headline} (${AUDIENCES[input.audience]})\n${result.summary}`;
  const body = result.checks
    .map((c) => `[${label[c.severity]}] ${c.title} (${src[c.source]})\n${c.detail}${c.fix ? `\n→ ${c.fix}` : ''}`)
    .join('\n\n');
  return `${head}\n\n${body}`;
}
